import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../themes';
import { useTranslation } from 'react-i18next';

// Make sure boxplot module is loaded
import HighchartsMore from 'highcharts/highcharts-more';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';
HighchartsMore(Highcharts);

const HourAnalysisChart = ({ data, viewMode, isLoading }) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: viewMode === 'box' ? 'boxplot' : 'column',
      height: chartHeight,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      title: {
        text: t('hour_analysis.x_axis_title'),
      },
    },
    yAxis: {
      title: {
        text: 'kWh',
      },
    },
    tooltip: {
      formatter: function () {
        if (viewMode === 'box') {
          return `
            <b>${this.key}</b><br/>
            Min: ${this.point.low} kWh<br/>
            Q1: ${this.point.q1} kWh<br/>
            Median: ${this.point.median} kWh<br/>
            Q3: ${this.point.q3} kWh<br/>
            Max: ${this.point.high} kWh
          `;
        } else {
          return `<b>${this.x}</b><br/>${t('common.mean')}: ${formatNumber(
            this.y,
          )}`;
        }
      },
    },
    series: [
      {
        name: viewMode === 'box' ? 'BoxPlot' : (data?.Name ?? t('common.mean')),
        type: viewMode === 'box' ? 'boxplot' : 'column',
        data: [],
        color: viewMode === 'box' ? undefined : '#7cb5ec',
      },
    ],
    credits: {
      enabled: false,
    },
  });

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!data?.HourAnalysisData || !chart) return;

    const xCategories = Array.from({ length: 24 }, (_, i) => `${i}:00`);

    // Tooltip formatter based on viewMode
    const tooltipFormatter = function () {
      const hourLabel = xCategories[this.point.index] ?? this.x;

      if (viewMode === 'box') {
        return `
          <b>${hourLabel}</b><br/>
          <span style="color:#888">Min:</span> ${formatNumber(
            this.point.low,
          )} kWh<br/>
          <span style="color:#888">Q1:</span> ${formatNumber(
            this.point.q1,
          )} kWh<br/>
          <span style="color:#888">Median:</span> ${formatNumber(
            this.point.median,
          )} kWh<br/>
          <span style="color:#888">Q3:</span> ${formatNumber(
            this.point.q3,
          )} kWh<br/>
          <span style="color:#888">Max:</span> ${formatNumber(
            this.point.high,
          )} kWh
        `;
      }

      // column viewMode
      return `<b>${hourLabel}</b><br/>${t('common.mean')}: ${formatNumber(
        this.y,
      )} kWh`;
    };

    if (viewMode === 'column') {
      const meanSeries = Array(24).fill(null);

      data.HourAnalysisData.forEach((item) => {
        if (item.HourOfDay >= 0 && item.HourOfDay < 24) {
          meanSeries[item.HourOfDay] = item.MeanValue ?? null;
        }
      });

      chart.update(
        {
          chart: { type: 'column' },
          tooltip: {
            formatter: tooltipFormatter,
          },
          series: [
            {
              name: data.Name,
              type: 'column',
              data: meanSeries,
              color:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
              borderWidth: 0, // ✅ No border
            },
          ],
        },
        true,
        true,
      );
    }

    if (viewMode === 'box') {
      const boxPlotSeries = [];

      data.HourAnalysisData.forEach((item) => {
        if (
          item.HourOfDay >= 0 &&
          item.HourOfDay < 24 &&
          item.MinValue != null &&
          item.Q1Value != null &&
          item.MedianValue != null &&
          item.Q3Value != null &&
          item.MaxValue != null
        ) {
          boxPlotSeries[item.HourOfDay] = [
            item.MinValue,
            item.Q1Value,
            item.MedianValue,
            item.Q3Value,
            item.MaxValue,
          ];
        } else {
          boxPlotSeries[item.HourOfDay] = null;
        }
      });

      chart.update(
        {
          chart: { type: 'boxplot' },
          tooltip: {
            formatter: tooltipFormatter,
          },
          series: [
            {
              name: data.Name,
              type: 'boxplot',
              data: boxPlotSeries,
            },
          ],
          plotOptions: {
            boxplot: {
              fillColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 0.1)'
                  : 'hsla(67, 100%, 40%, 0.2)',
              lineWidth: 1.5,
              medianColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
              stemColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
              whiskerColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
            },
          },
        },
        true,
        true,
      );
    }
  }, [data, viewMode]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
          plotOptions: {
            boxplot: {
              fillColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 0.1)'
                  : 'hsla(67, 100%, 40%, 0.2)',
              lineWidth: 1.5,
              medianColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
              stemColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
              whiskerColor:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
            },
          },
          series: [
            {
              color:
                theme.palette.mode === 'dark'
                  ? 'hsla(67, 100%, 40%, 1)'
                  : 'hsla(125, 85%, 30%, 1)',
            },
          ],
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartComponentRef}
    />
  );
};

export default HourAnalysisChart;
